import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Save, RotateCcw, FileText, Activity, Eye } from "lucide-react";
import { SectionWorkspace } from "./SectionWorkspace";
import type { TemplateDetail } from "@/hooks/use-admin-templates";
import type { AssignedStructure, BodyRegionStructure } from "@/hooks/use-body-region-structures";
import type { CreateCustomSectionInput, CreateCustomStructureInput } from "@/hooks/use-custom-sections-structures";
import type { CombinedSectionItem } from "@/hooks/use-combined-section-pool";

// XR view count helpers
const VIEW_COUNT_OPTIONS = [1, 2, 3, 4, 5] as const;
const VIEW_WORD_MAP: Record<number, string> = { 1: "Single", 2: "2", 3: "3", 4: "4", 5: "5" };

/** Extract the body part from XR technique text, e.g. "PA and lateral views of the chest were obtained." -> "chest" */
function parseXrBodyPart(text: string): string | null {
  const match = text.match(/views?\s+of\s+the\s+(.+?)\s+(?:was|were)\s+obtained/i);
  return match ? match[1] : null;
}

/** Detect approximate view count from technique text */
function parseXrViewCount(text: string): number | null {
  const lower = text.toLowerCase();
  if (/single\s+view/i.test(lower)) return 1;
  const numMatch = lower.match(/^(\d+)\s+views?\b/);
  if (numMatch) return parseInt(numMatch[1], 10);
  // Count comma/and-separated view descriptors before "view(s)"
  const descMatch = text.match(/^(.+?)\s+views?\s+of/i);
  if (descMatch) {
    const desc = descMatch[1];
    const parts = desc.split(/,\s*|\s+and\s+/).filter(Boolean);
    return parts.length;
  }
  return null;
}

/** Generate technique text for a given view count and body part */
function generateXrTechnique(viewCount: number, bodyPart: string): string {
  const word = VIEW_WORD_MAP[viewCount] || String(viewCount);
  const verb = viewCount === 1 ? "was" : "were";
  const viewNoun = viewCount === 1 ? "view" : "views";
  return `${word} ${viewNoun} of the ${bodyPart} ${verb} obtained.`;
}

interface TemplateEditorProps {
  detail: TemplateDetail | null;
  loading: boolean;
  updating: boolean;
  combinedSections: CombinedSectionItem[];
  combinedSectionsLoading: boolean;
  onUpdateExam: (displayName: string, isActive: boolean) => Promise<boolean>;
  onUpdateTechnique: (techniqueText: string) => Promise<boolean>;

  // SectionWorkspace callbacks
  onUpdateSectionTitle: (token: string, title: string) => Promise<boolean>;
  onUpdateDefaultText: (token: string, text: string) => Promise<boolean>;
  onDeleteSection: (token: string) => Promise<boolean>;
  onAddFromPool: (section: CombinedSectionItem) => Promise<boolean>;
  onReorderSections: (orderedTokens: string[]) => Promise<boolean>;
  onToggleSectionEnabled: (token: string, enabled: boolean) => Promise<boolean>;
  onRestoreDefaultSections: () => Promise<boolean>;

  // Structure management callbacks
  assignedStructures?: AssignedStructure[];
  availableStructures?: BodyRegionStructure[];
  structureSearchResults?: BodyRegionStructure[];
  structuresLoading?: boolean;
  structureSearchLoading?: boolean;
  onToggleStructure?: (sectionToken: string, structureKey: string, include: boolean) => Promise<boolean>;
  onStructureSearch?: (query: string) => void;
  onClearStructureSearch?: () => void;

  // Custom section/structure creation
  onCreateCustomSection?: (input: CreateCustomSectionInput) => Promise<unknown>;
  onCreateCustomStructure?: (input: CreateCustomStructureInput) => Promise<unknown>;
}

export function TemplateEditor({
  detail,
  loading,
  updating,
  combinedSections,
  combinedSectionsLoading,
  onUpdateExam,
  onUpdateTechnique,
  onUpdateSectionTitle,
  onUpdateDefaultText,
  onDeleteSection,
  onAddFromPool,
  onReorderSections,
  onToggleSectionEnabled,
  onRestoreDefaultSections,
  // Structure props
  assignedStructures = [],
  availableStructures = [],
  structureSearchResults = [],
  structuresLoading = false,
  structureSearchLoading = false,
  onToggleStructure,
  onStructureSearch,
  onClearStructureSearch,
  // Custom creation props
  onCreateCustomSection,
  onCreateCustomStructure,
}: TemplateEditorProps) {
  // Local state for editing
  const [displayName, setDisplayName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [techniqueText, setTechniqueText] = useState("");

  // Track dirty state
  const [examDirty, setExamDirty] = useState(false);
  const [techniqueDirty, setTechniqueDirty] = useState(false);

  // XR views: detect modality and parse current view count
  const isXrModality = detail?.exam.modality === "XR";
  const xrBodyPart = useMemo(() => {
    if (!isXrModality || !techniqueText) return null;
    return parseXrBodyPart(techniqueText);
  }, [isXrModality, techniqueText]);
  const xrViewCount = useMemo(() => {
    if (!isXrModality || !techniqueText) return null;
    return parseXrViewCount(techniqueText);
  }, [isXrModality, techniqueText]);

  // Compute enabled tokens set and isCustomized from detail
  const enabledTokens = useMemo(() => {
    if (!detail) return new Set<string>();
    const config = detail.exam.custom_sections ?? detail.exam.default_sections ?? [];
    return new Set(config.map(s => s.section_token));
  }, [detail]);

  const isCustomized = useMemo(() => {
    if (!detail) return false;
    return detail.exam.custom_sections !== null;
  }, [detail]);

  // Sync local state when detail changes
  useEffect(() => {
    if (detail) {
      setDisplayName(detail.exam.display_name);
      setIsActive(detail.exam.is_active);
      setTechniqueText(detail.technique?.technique_text || "");
      setExamDirty(false);
      setTechniqueDirty(false);
    }
  }, [detail]);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <FileText className="h-16 w-16 mb-4 opacity-30" />
        <p className="text-lg font-medium">Select a template to edit</p>
        <p className="text-sm mt-1">Choose a template from the list on the left</p>
      </div>
    );
  }

  const handleSaveExam = async () => {
    const success = await onUpdateExam(displayName, isActive);
    if (success) setExamDirty(false);
  };

  const handleSaveTechnique = async () => {
    const success = await onUpdateTechnique(techniqueText);
    if (success) setTechniqueDirty(false);
  };

  const handleResetExam = () => {
    setDisplayName(detail.exam.display_name);
    setIsActive(detail.exam.is_active);
    setExamDirty(false);
  };

  const handleResetTechnique = () => {
    setTechniqueText(detail.technique?.technique_text || "");
    setTechniqueDirty(false);
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-6">
        {/* Exam Info Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Exam Info</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                {examDirty && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleResetExam}
                      disabled={updating}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveExam}
                      disabled={updating}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      Save
                    </Button>
                  </>
                )}
              </div>
            </div>
            <CardDescription>
              ID: <code className="text-xs bg-muted px-1 py-0.5 rounded">{detail.exam.exam_family_id}</code>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name</Label>
                <Input
                  id="displayName"
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    setExamDirty(true);
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    checked={isActive}
                    onCheckedChange={(checked) => {
                      setIsActive(checked);
                      setExamDirty(true);
                    }}
                  />
                  <span className="text-sm">{isActive ? "Active" : "Inactive"}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Badge variant="outline">{detail.exam.modality}</Badge>
              <Badge variant="outline">{detail.exam.body_region}</Badge>
              {detail.exam.contrast_phase && (
                <Badge variant="outline">{detail.exam.contrast_phase}</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Technique Card */}
        {detail.technique && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg">Technique</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  {techniqueDirty && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetTechnique}
                        disabled={updating}
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Reset
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveTechnique}
                        disabled={updating}
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* XR Default Views selector */}
              {isXrModality && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Eye className="h-4 w-4" />
                    <span>Default Views</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {VIEW_COUNT_OPTIONS.map((count) => (
                      <Button
                        key={count}
                        variant={xrViewCount === count ? "default" : "outline"}
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => {
                          const bodyPart = xrBodyPart || detail.exam.body_region?.toLowerCase() || "region";
                          const newText = generateXrTechnique(count, bodyPart);
                          setTechniqueText(newText);
                          setTechniqueDirty(true);
                        }}
                      >
                        {count}
                      </Button>
                    ))}
                  </div>
                  {xrViewCount && (
                    <span className="text-xs text-muted-foreground">
                      ({xrViewCount} view{xrViewCount !== 1 ? "s" : ""} detected)
                    </span>
                  )}
                </div>
              )}
              <Textarea
                value={techniqueText}
                onChange={(e) => {
                  setTechniqueText(e.target.value);
                  setTechniqueDirty(true);
                }}
                placeholder="Enter technique text..."
                className="min-h-[100px] font-mono text-sm"
              />
            </CardContent>
          </Card>
        )}

        {/* Unified Section Workspace */}
        <SectionWorkspace
          mode="admin"
          sections={detail.sections}
          enabledTokens={enabledTokens}
          availablePoolSections={combinedSections}
          poolLoading={combinedSectionsLoading}
          updating={updating}
          onUpdateSectionTitle={onUpdateSectionTitle}
          onUpdateDefaultText={onUpdateDefaultText}
          onDeleteSection={onDeleteSection}
          onAddFromPool={onAddFromPool}
          onReorderSections={onReorderSections}
          onToggleEnabled={onToggleSectionEnabled}
          onRestoreDefaults={onRestoreDefaultSections}
          isCustomized={isCustomized}
          // Structure props
          assignedStructures={assignedStructures}
          availableStructures={availableStructures}
          structureSearchResults={structureSearchResults}
          structuresLoading={structuresLoading}
          structureSearchLoading={structureSearchLoading}
          onToggleStructure={onToggleStructure}
          onStructureSearch={onStructureSearch}
          onClearStructureSearch={onClearStructureSearch}
          // Custom creation props
          examFamilyId={detail.exam.exam_family_id}
          onCreateCustomSection={onCreateCustomSection}
          onCreateCustomStructure={onCreateCustomStructure}
        />
      </div>
    </ScrollArea>
  );
}
